import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  diagnoseOcppTransactionLinkingOptions,
  getTransactionsWithEventsForSiteOptions,
} from "../../../services/client/@tanstack/react-query.gen";
import type {
  OcppStartCandidate,
  TransactionLinkingDiagnosis,
  TransactionViewDto,
} from "../../../services/client/types.gen";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import { formatDateTime } from "../../../util/formatDate";
import {
  formatDuration,
  formatOffset,
  MESSAGE_TIME_FORMAT,
  START_PROBLEMS,
  TRANSACTION_PROBLEMS,
} from "./constants";
import { useOcppMessageFilters } from "./useOcppMessageFilters";

const CONTEXT_MINUTES = 2;

const transactionLabel = (transaction: TransactionViewDto) =>
  `${transaction.asset_id} · ${formatDateTime(transaction.started_at)} · ${
    transaction.badge_id ?? "no badge"
  }`;

const Verdict = ({ diagnosis }: { diagnosis: TransactionLinkingDiagnosis }) => {
  if (diagnosis.linked_ocpp_start) {
    return (
      <Alert
        type="success"
        showIcon
        message={`Linked to badge ${diagnosis.linked_ocpp_start.badge_id} through OCPP transaction ${diagnosis.linked_ocpp_start.ocpp_transaction_id}.`}
      />
    );
  }

  if (diagnosis.problems.length > 0) {
    return (
      <Alert
        type="error"
        showIcon
        message="This transaction cannot be linked"
        description={
          <ul className="m-0 pl-4">
            {diagnosis.problems.map((problem) => (
              <li key={problem}>
                <strong>{TRANSACTION_PROBLEMS[problem].label}:</strong>{" "}
                {TRANSACTION_PROBLEMS[problem].description}
              </li>
            ))}
          </ul>
        }
      />
    );
  }

  const [closest] = diagnosis.candidates;
  if (closest && closest.problems.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message="An OCPP start meets every matching rule, but no badge is linked"
        description={`Linking retries for a while after the start, so a recent session may still be linked. Otherwise another MQTT transaction on ${diagnosis.asset_id} started closer to this OCPP start and took the badge. Check the Transactions tab for a second session around the same time.`}
      />
    );
  }

  return (
    <Alert
      type="error"
      showIcon
      message="No OCPP start meets every matching rule"
      description={
        closest
          ? `The closest start (${closest.socket_id} connector ${closest.evse_id ?? "?"}) fails on: ${closest.problems
              .map((problem) => START_PROBLEMS[problem].label.toLowerCase())
              .join(", ")}.`
          : undefined
      }
    />
  );
};

export const TransactionLinkingPanel = ({ site_id }: { site_id: string }) => {
  const filters = useOcppMessageFilters();
  const [onlyWithoutBadge, setOnlyWithoutBadge] = useState(true);
  const [typedTransactionId, setTypedTransactionId] = useState("");

  const transactionsQuery = useQuery(
    getTransactionsWithEventsForSiteOptions({
      path: { site_id },
      query: { page_size: 100 },
    }),
  );

  const diagnosisQuery = useQuery({
    ...diagnoseOcppTransactionLinkingOptions({
      path: { site_id, transaction_id: filters.transactionId ?? "" },
    }),
    enabled: !!filters.transactionId,
  });

  const transactions = (transactionsQuery.data?.data ?? []).filter(
    (transaction) => !onlyWithoutBadge || !transaction.badge_id,
  );
  const transactionOptions = [
    ...transactions.map((transaction) => ({
      value: transaction.transaction_id,
      label: transactionLabel(transaction),
      searchText: `${transaction.transaction_id} ${transactionLabel(transaction)}`,
    })),
    ...(typedTransactionId &&
    !transactions.some(
      (transaction) => transaction.transaction_id === typedTransactionId,
    )
      ? [
          {
            value: typedTransactionId,
            label: `Transaction id ${typedTransactionId}`,
            searchText: typedTransactionId,
          },
        ]
      : []),
  ];

  const showMessagesAround = (candidate: OcppStartCandidate) => {
    const receivedAt = dayjs(candidate.received_at);
    filters.update({
      search: "",
      actions: [],
      sender: null,
      chargers: [candidate.socket_id],
      range: {
        from: receivedAt.subtract(CONTEXT_MINUTES, "minute").toISOString(),
        to: receivedAt.add(CONTEXT_MINUTES, "minute").toISOString(),
      },
    });
  };

  const diagnosis = diagnosisQuery.data;
  const matchWindowSeconds = diagnosis?.match_window_seconds ?? 0;

  const columns: ColumnsType<OcppStartCandidate> = [
    {
      title: "Charger · connector",
      key: "connector",
      render: (_, candidate) => (
        <Space size={4}>
          <Typography.Text code>{candidate.socket_id}</Typography.Text>
          <span>#{candidate.evse_id ?? "?"}</span>
          {candidate.is_linked && <Tag color="green">Linked</Tag>}
        </Space>
      ),
    },
    {
      title: "Maps to asset",
      dataIndex: "resolved_asset_id",
      key: "resolved_asset_id",
      render: (assetId: string | null) =>
        assetId ? (
          <Tag color={assetId === diagnosis?.asset_id ? "green" : "red"}>
            {assetId}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Badge (idTag)",
      key: "id_token",
      render: (_, candidate) => (
        <Space size={4}>
          <span>{candidate.id_token ?? "—"}</span>
          {candidate.id_token &&
            candidate.id_token === candidate.ocpp_transaction_id && (
              <Tooltip title="The charger sent its transaction id as idTag; this is a placeholder, not a real badge.">
                <Tag color="orange">placeholder</Tag>
              </Tooltip>
            )}
          {candidate.authorization_status && (
            <Tag
              color={
                candidate.authorization_status === "Accepted"
                  ? "default"
                  : "orange"
              }
            >
              {candidate.authorization_status}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "OCPP transaction",
      dataIndex: "ocpp_transaction_id",
      key: "ocpp_transaction_id",
      render: (transactionId: string | null) => transactionId ?? "—",
    },
    {
      title: "Charger start time",
      key: "charger_started_at",
      render: (_, candidate) =>
        candidate.charger_started_at ? (
          <Tooltip
            title={`Received by the broker at ${dayjs(candidate.received_at).format(MESSAGE_TIME_FORMAT)}`}
          >
            <span className="whitespace-nowrap">
              {dayjs(candidate.charger_started_at).format(MESSAGE_TIME_FORMAT)}
            </span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: (
        <Tooltip
          title={`Charger start time minus MQTT start time. Matching allows ±${formatDuration(matchWindowSeconds)}.`}
        >
          Offset
        </Tooltip>
      ),
      dataIndex: "offset_seconds",
      key: "offset_seconds",
      render: (offsetSeconds: number | null) =>
        offsetSeconds === null ? (
          "—"
        ) : (
          <Typography.Text
            type={
              Math.abs(offsetSeconds) > matchWindowSeconds
                ? "danger"
                : undefined
            }
            className="whitespace-nowrap"
          >
            {formatOffset(offsetSeconds)}
          </Typography.Text>
        ),
    },
    {
      title: "Why it does not match",
      key: "problems",
      render: (_, candidate) =>
        candidate.problems.length === 0 ? (
          <Tag color="green">Matches</Tag>
        ) : (
          <Space size={[4, 4]} wrap>
            {candidate.problems.map((problem) => (
              <Tooltip
                key={problem}
                title={START_PROBLEMS[problem].description}
              >
                <Tag color="red">{START_PROBLEMS[problem].label}</Tag>
              </Tooltip>
            ))}
          </Space>
        ),
    },
    {
      key: "actions",
      render: (_, candidate) => (
        <Button size="small" onClick={() => showMessagesAround(candidate)}>
          Show messages
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <Typography.Paragraph type="secondary" className="mb-0">
        Pick a transaction we received over MQTT to see the OCPP starts the
        broker saw around it and why each one did or did not link its badge.
      </Typography.Paragraph>

      <Space wrap>
        <Select
          showSearch
          allowClear
          className="w-[32rem] max-w-full"
          placeholder="Select a transaction or paste its id"
          loading={transactionsQuery.isLoading}
          value={filters.transactionId}
          options={transactionOptions}
          filterOption={(input, option) =>
            option?.searchText.toLowerCase().includes(input.toLowerCase()) ??
            false
          }
          onSearch={(value) => setTypedTransactionId(value.trim())}
          onChange={(value: string | undefined) =>
            filters.setTransactionId(value ?? null)
          }
        />
        <Space size={8}>
          <Switch
            size="small"
            checked={onlyWithoutBadge}
            onChange={setOnlyWithoutBadge}
          />
          <Typography.Text>Only transactions without a badge</Typography.Text>
        </Space>
      </Space>

      {!filters.transactionId ? null : diagnosisQuery.isLoading ? (
        <Spin />
      ) : diagnosisQuery.error ? (
        <Alert
          type="error"
          showIcon
          message="Could not diagnose this transaction"
          description={extractErrorMessage(diagnosisQuery.error)}
        />
      ) : diagnosis ? (
        <div className="space-y-4">
          <Verdict diagnosis={diagnosis} />

          <Descriptions size="small" bordered column={{ xs: 1, md: 2, xl: 3 }}>
            <Descriptions.Item label="Asset">
              {diagnosis.asset_id}
            </Descriptions.Item>
            <Descriptions.Item label="MQTT start">
              {diagnosis.started_at
                ? dayjs(diagnosis.started_at).format(MESSAGE_TIME_FORMAT)
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Badge from MQTT">
              {diagnosis.mqtt_badge_id ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Linked badge">
              {diagnosis.linked_ocpp_start ? (
                <Tag color="green">{diagnosis.linked_ocpp_start.badge_id}</Tag>
              ) : (
                <Tag color="red">Not linked</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Broker mode">
              {diagnosis.broker_mode}
            </Descriptions.Item>
            <Descriptions.Item label="Mapped OCPP connectors">
              {diagnosis.mapped_connectors.length > 0 ? (
                <Space size={[4, 4]} wrap>
                  {diagnosis.mapped_connectors.map((connector) => (
                    <Tag key={`${connector.socket_id}#${connector.evse_id}`}>
                      {connector.socket_id} #{connector.evse_id}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Tag color="red">None</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Table<OcppStartCandidate>
            size="small"
            columns={columns}
            dataSource={diagnosis.candidates}
            rowKey={(candidate) =>
              `${candidate.socket_id}:${candidate.message_id}`
            }
            pagination={false}
            title={() =>
              `OCPP starts received within ${formatDuration(diagnosis.search_window_seconds)} of the transaction start`
            }
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No OCPP starts were received around this transaction"
                />
              ),
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
